import {
  EntityType,
  GetQuestionCreationTaskByIdDocument,
  GetStepTransitionsByTaskIdDocument,
  QuestionStatus,
  useFinishQuestionCreationTaskMutation,
  useGetStepTransitionsByTaskIdQuery,
  useGetTaskQuestionGroupByIdsQuery,
} from '@exam-management/graphql';
import { useParams } from '@exam-management/router';
import { TaskOverdueDialog, isTaskOverdueError } from '@exam-management/task-management';
import { ConfirmationModal } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';

import { useQuestionCreationTaskDetails } from '../../providers';

export interface FinishTaskConfirmationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function FinishTaskConfirmationModal(
  props: Readonly<FinishTaskConfirmationModalProps>,
) {
  const { open, onClose } = props;
  const { taskId } = useParams('/my-task/question-creation/:taskId');
  const { showSnackbar } = useSnackbar();
  const [isOverdueDialogOpen, overdueDialogHandlers] = useDisclosure();
  const { task } = useQuestionCreationTaskDetails();
  const { taskEntities } = task;
  const questionGroupIds =
    task.metadata?.requirement?.itemSetCreation?.map((itemSet) => itemSet.questionGroupId) ?? [];

  const { data: taskQuestionGroups } = useGetTaskQuestionGroupByIdsQuery({
    variables: {
      questionGroupIds,
      taskId: task.id,
    },
  });

  const { data } = useGetStepTransitionsByTaskIdQuery({
    variables: {
      getStepTransitionsByTaskIdInput: {
        taskId,
      },
    },
  });

  const [finishTask, { loading: isLoading }] = useFinishQuestionCreationTaskMutation({
    variables: {
      finishQuestionCreationTaskInput: {
        taskId,
        workflowStepId: data?.wflGetStepTransitionsByTaskId.steps?.[0]?.id ?? '',
        metadata: {
          questions: [
            ...(taskEntities
              ?.filter((taskEntity) => taskEntity.entityType === EntityType.Question)
              .map((taskEntity) => ({
                id: taskEntity.entityId,
                status: QuestionStatus.PreDraft,
                auditors: {},
              })) ?? []),
            ...(taskQuestionGroups?.qctGetTaskQuestionGroupByIds.flatMap(
              (questionGroup) =>
                questionGroup.questions?.map((question) => ({
                  id: question.id,
                  status: QuestionStatus.PreDraft,
                  auditors: {},
                })) ?? [],
            ) ?? []),
          ],
        },
      },
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetStepTransitionsByTaskIdDocument,
        variables: {
          getStepTransitionsByTaskIdInput: {
            taskId,
          },
        },
      },
      {
        query: GetQuestionCreationTaskByIdDocument,
        variables: {
          getTaskByIdInput: {
            taskId,
          },
        },
      },
    ],
  });

  const handleOnConfirm = async () => {
    await finishTask({
      onCompleted: () => {
        showSnackbar({
          message: 'Task completed successfully',
          variant: 'success',
        });
        onClose?.();
      },
      onError: (error) => {
        if (isTaskOverdueError(error)) {
          onClose?.();
          overdueDialogHandlers.open();
          return;
        }
        showSnackbar({
          message: 'Failed to finish task',
          variant: 'error',
        });
        onClose?.();
      },
    });
  };

  return (
    <>
      <ConfirmationModal
        open={open}
        onClose={isLoading ? undefined : onClose}
        onAccept={handleOnConfirm}
        onDiscard={onClose}
        title='Finish Task?'
        content='คุณต้องการส่งคำถามทั้งหมดและปิดงานนี้ใช่หรือไม่'
        acceptText='Finish'
        loading={isLoading}
        variant='primary'
      />
      <TaskOverdueDialog
        variant='submit-review'
        open={isOverdueDialogOpen}
        onClose={overdueDialogHandlers.close}
      />
    </>
  );
}
